import { Head, Link } from "@inertiajs/react";
import { CheckCircle, Package, Truck, CreditCard, Home } from "lucide-react";
import { product_path, home_path } from "@/routes.js";
import formatMoney from "~/utils/formatMoney.js";
import pageTitle from "~/utils/pageTitle.js";
import { Button } from "~/components/ui/button.jsx";
import { Badge } from "~/components/ui/badge.jsx";
import { Alert, AlertDescription } from "~/components/ui/alert.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "~/components/ui/card.jsx";
import { Heading } from "@/components/ui/heading.jsx";

const propertiesBlackList = [
  "Минимальное количество",
  "Праздник1",
  "Скидка не распостраняется",
];

export default function Complete({ order, auth }) {
  const {
    number,
    state,
    total,
    email,
    line_items = [],
    item_total = 0,
    shipment_total = 0,
    additional_tax_total = 0,
    promo_total = 0,
    ship_address,
    bill_address,
    completed_at,
  } = order;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAddress = (address) => {
    if (!address) return null;
    
    const parts = [
      address.firstname && address.lastname ? `${address.firstname} ${address.lastname}` : null,
      address.address1,
      address.city,
      address.stateName,
      address.zipcode,
      address.country,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : null;
  };

  return (
    <>
      <Head title={pageTitle("Заказ успешно оформлен")} />
      
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          
          <Heading size="h1" className="mb-4">
            Спасибо за ваш заказ!
          </Heading>
          
          <p className="text-gray-600 text-lg mb-2">
            Ваш заказ №{number} успешно оформлен
          </p>
          
          <Badge variant="secondary" className="mb-4">
            {state === "complete" ? "Выполнен" : "В обработке"}
          </Badge>
          
          {completed_at && (
            <p className="text-sm text-gray-500">
              Оформлен: {formatDate(completed_at)}
            </p>
          )}
        </div>

        <div className="flex flex-wrap space-y-8">
          {/* Order Details - Left Column */}
          <div className="w-full xl:w-2/3 xl:pr-8 space-y-8 order-2 xl:order-1">
            
            {/* Products List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Товары в заказе
                </CardTitle>
                <CardDescription>
                  {line_items.length} {line_items.length === 1 ? "товар" : line_items.length < 5 ? "товара" : "товаров"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {line_items.map((item) => {
                    const { quantity, product, variant, price, pre_tax_amount } = item;
                    const { name, slug, thumbnail, product_properties } = product;
                    const image = thumbnail?.url;

                    return (
                      <div key={item.id} className="flex items-start gap-4 pb-6 border-b last:border-b-0 last:pb-0">
                        {/* Product Image */}
                        <Link href={product_path(slug)} className="flex-shrink-0">
                          {image ? (
                            <img
                              src={image}
                              alt={name}
                              loading="lazy"
                              className="w-20 h-20 rounded-lg object-cover border"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-lg bg-gray-100 border flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </Link>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-2">
                            <Link 
                              href={product_path(slug)}
                              className="hover:text-primary transition-colors"
                            >
                              {name}
                            </Link>
                          </h3>

                          {/* Product Properties */}
                          {product_properties && product_properties.length > 0 && (
                            <dl className="text-xs text-gray-600 space-y-1 mb-3">
                              {product_properties
                                .filter(
                                  (pp) =>
                                    !propertiesBlackList.includes(pp.property_presentation)
                                )
                                .map((pp, index) => (
                                  <div key={index} className="flex gap-2">
                                    <dt className="font-medium">{pp.property_presentation}:</dt>
                                    <dd>{pp.value}</dd>
                                  </div>
                                ))}
                            </dl>
                          )}

                          {/* Price and Quantity */}
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              {formatMoney(price)} × {quantity} шт.
                            </div>
                            <div className="font-semibold text-gray-900">
                              {formatMoney(pre_tax_amount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Customer Information */}
            {(email || ship_address || bill_address) && (
              <Card>
                <CardHeader>
                  <CardTitle>Информация о заказе</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    {email && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Контактные данные</h4>
                        <p className="text-sm text-gray-600">{email}</p>
                      </div>
                    )}

                    {/* Shipping Address */}
                    {ship_address && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          Адрес доставки
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatAddress(ship_address)}
                        </p>
                      </div>
                    )}

                    {/* Billing Address */}
                    {bill_address && (!ship_address || formatAddress(bill_address) !== formatAddress(ship_address)) && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Платежный адрес
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatAddress(bill_address)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary - Right Column */}
          <div className="w-full xl:w-1/3 space-y-8 order-1 xl:order-2">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle>Итого по заказу</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Товары</span>
                    <span>{formatMoney(item_total)}</span>
                  </div>

                  {/* Shipping */}
                  {shipment_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Доставка</span>
                      <span>{formatMoney(shipment_total)}</span>
                    </div>
                  )}

                  {/* Tax */}
                  {additional_tax_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Налоги</span>
                      <span>{formatMoney(additional_tax_total)}</span>
                    </div>
                  )}

                  {/* Discounts */}
                  {promo_total < 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Скидки</span>
                      <span>{formatMoney(promo_total)}</span>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>К оплате</span>
                      <span className="text-primary">{formatMoney(total)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="pt-0">
                <Button 
                  asChild
                  size="xl" 
                  color="pink" 
                  className="w-full font-bold uppercase"
                >
                  <Link href={home_path()}>
                    <Home className="h-4 w-4 mr-2" />
                    Продолжить покупки
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Success Alert */}
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Мы отправили подтверждение заказа на вашу электронную почту. 
                Вы можете отслеживать статус заказа в личном кабинете.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </>
  );
}